import React from 'react';
import {
  Container,
  Header,
  Table,
  Box,
  Link,
  Icon,
  Spinner
} from '@cloudscape-design/components';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { fetchAuthSession } from 'aws-amplify/auth';
import useCache from '../hooks/useCache';
import CacheIndicator from './CacheIndicator';
import config from '../config';

const RecentView = ({ userHomePath, onVideoSelect }) => {

  const loadRecentItems = async () => {
    const session = await fetchAuthSession();
    const lambda = new LambdaClient({
      region: config.Region,
      credentials: session.credentials
    });

    const command = new InvokeCommand({
      FunctionName: config.RecordingsIndexFunctionArn,
      Payload: JSON.stringify({
        action: 'getRecent',
        limit: 10
      })
    });

    const response = await lambda.send(command);
    const result = JSON.parse(new TextDecoder().decode(response.Payload));

    if (result.statusCode === 200) {
      const data = JSON.parse(result.body);
      return (data.items || []).map(item => ({
        name: item.s3Key.split('/').pop(),
        displayName: item.displayName || item.s3Key.split('/').pop(),
        key: item.s3Key,
        type: 'file',
        size: item.size,
        isVideo: true,
        timestamp: item.createdAt
      }));
    }
    throw new Error('Failed to load recent recordings');
  };

  const { data: recentItems, loading: isLoading, cachedAt, isCached, refresh } = useCache(
    `recent-${userHomePath}`, loadRecentItems, { enabled: !!userHomePath && !!config.RecordingsIndexFunctionArn }
  );

  const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const columnDefinitions = [
    {
      id: 'name',
      header: 'Name',
      cell: item => (
        <Link href="#" onFollow={() => onVideoSelect(item)}>
          <Icon name="video" /> {item.displayName}
        </Link>
      ),
      sortingField: 'displayName',
      isRowHeader: true
    },
    {
      id: 'size',
      header: 'Size',
      cell: item => formatFileSize(item.size),
      sortingField: 'size'
    },
    {
      id: 'timestamp',
      header: 'Created',
      cell: item => formatDate(item.timestamp),
      sortingField: 'timestamp'
    }
  ];

  return (
    <Container variant='embedded'
      header={
        <Header variant="h1" description="Your 10 most recent recordings"
          actions={<CacheIndicator cachedAt={cachedAt} isCached={isCached} loading={isLoading} onRefresh={refresh} />}
        >
          Recent Recordings
        </Header>
      }
    >
      {isLoading && !recentItems ? (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" padding={{ top: 's' }}>
            Loading recent recordings...
          </Box>
        </Box>
      ) : (
        <Table
          columnDefinitions={columnDefinitions}
          items={recentItems || []}
          empty={
            <Box textAlign="center" padding="l">
              <b>No recordings found</b>
            </Box>
          }
        />
      )}
    </Container>
  );
};

export default RecentView;
